import {FC, useState} from 'react';

import {Box, Checkbox, Typography, TypographyProps} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {When} from 'react-if';

import BasketVoucherIcon from 'assets/icons/basketVoucherIcon';
import ChevronDown from 'assets/icons/chevronDown';
import ChevronRight from 'assets/icons/chevronRight';
import ThickChevronUp from 'assets/icons/thickChevronUp';
import VoucherDiscountIcon from 'assets/icons/voucherDiscountIcon';
import {Button} from 'components/button/Button';
import {themeConfig} from 'config/theme';
import useAnalytics, {ANALYTICS, EVENT_SOURCE, SUGGESTEDLIST_CHANNELS} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {formatPriceWithCurrency} from 'utils';
import {filter1PItems, filter3PItems} from 'utils/helper';
import {BasketMinimumOrderBanner} from 'views/basket-banners/components/BasketMinimumOrderBanner';
import {MinimumBasketAmountModal} from 'views/basket-modals/MinimumBasketAmountModal';

import {
  DeliveryFeesLabel,
  StyledBasketFooterWrapper,
  StyleFooterContentWrapper,
  VoucherDiscountContainer,
  VoucherDiscountText,
  VoucherLabel,
  VoucherSelectText,
  VoucherWrapper
} from './styles';
import {BasketDrawerFooterViewProps} from './types/basketDrawerFooter.type';

export const BasketTotalPriceV2 = styled(Typography)<TypographyProps>(({theme}) => ({
  fontWeight: theme.typography.h2.fontWeight,
  fontSize: theme.typography.h4.fontSize
}));

export const FooterView: FC<BasketDrawerFooterViewProps> = ({
  onCheckoutClick,
  actionButtonLabel,
  basketPrice,
  disableCheckoutBtn,
  loading,
  hasProductInBasket,
  deliveryFees,
  isLessThanMinSpend,
  selectedItems,
  onClickSelectAllItems,
  isSelectedAllBasketItems,
  setShowBasketSummaryModal,
  shouldShowMinBasketWarning,
  minimumBasket,
  showBasketSummaryModal,
  showDeliveryFee
}) => {
  const theme = useTheme();
  const {t} = useTranslation();
  const [isMinimalBasketModalOpen, setIsMinimalBasketModalOpen] = useState<boolean>(false);
  const isReactNativeWebView = ReactNative.isReactNativeWebView();
  const analytics = useAnalytics();
  const basket = useAppSelector((state) => state?.basket?.basket);
  const allBasketItems = basket?.basketItems || [];

  const toggleMinimalBasketModal = () => {
    setIsMinimalBasketModalOpen((prev) => !prev);
  };

  const openBasketSummaryAnalytics = (eventName: string, payload: any) => {
    analytics.trackEvent(eventName, {
      storeId: basket?.storeCode || '',
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeWebView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0,
      ...payload
    });
  };

  const ChevronUpDownIcon = showBasketSummaryModal ? ChevronDown : ThickChevronUp;

  const discount =
    basket?.appliedVoucherPromotion?.reduce((total, voucher) => {
      return total + (voucher?.discount || 0);
    }, 0) || 0;

  const isOmniChannelEnabled = useAppSelector((state) => state?.basket?.omniChannelsVoucherRedemptionEnabled);

  return (
    <StyledBasketFooterWrapper>
      {hasProductInBasket && isReactNativeWebView && isOmniChannelEnabled && (
        <VoucherWrapper
          padding={isReactNativeWebView ? theme.spacing(1.5, 2, 1, 2) : theme.spacing(2, 4, 2, 4)}
          onClick={() => {
            SendBrowserEvent.goToBasketVouchers(allBasketItems);
          }}
        >
          <Box display="flex" alignItems="center" gap={theme.spacing(1)}>
            <BasketVoucherIcon size={20} />
            <VoucherLabel variant="body2">{t('omniChannelVoucher.voucher')}</VoucherLabel>
          </Box>

          <Box display="flex" alignItems="center" gap={theme.spacing(0.5)}>
            {discount ? (
              <VoucherDiscountContainer>
                <VoucherDiscountIcon size={48} />
                <VoucherDiscountText>-{discount}฿</VoucherDiscountText>
              </VoucherDiscountContainer>
            ) : (
              <VoucherSelectText>{t('omniChannelVoucher.select')}</VoucherSelectText>
            )}
            <ChevronRight width={16} height={16} fillColor={'#8E8E8E'} />
          </Box>
        </VoucherWrapper>
      )}
      <When condition={hasProductInBasket && isLessThanMinSpend}>
        <BasketMinimumOrderBanner />
      </When>
      <Box>
        <When condition={hasProductInBasket && !showBasketSummaryModal}>
          <Box
            padding={isReactNativeWebView ? theme.spacing(1.5, 2, 0, 2) : theme.spacing(2, 4, 0, 4)}
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            gap={theme.spacing(1)}
          >
            <Checkbox
              name="isDefault"
              onChange={onClickSelectAllItems}
              defaultChecked={isSelectedAllBasketItems}
              checked={isSelectedAllBasketItems}
              disabled={loading}
              data-test-id={'basket_select_all_checkbox'}
              sx={{
                '&.Mui-disabled': {color: theme.palette.primary.light},
                color: '#C7C7C7',
                '&.Mui-checked': {
                  color: 'primary'
                },
                height: 'fit-content',
                padding: 0
              }}
            />
            <Typography component="p" variant="body2">
              {isSelectedAllBasketItems ? t('flexiBasket.deselectAll') : t('flexiBasket.selectAll')}
            </Typography>
          </Box>
        </When>

        <StyleFooterContentWrapper isReactNativeView={isReactNativeWebView}>
          {basketPrice && hasProductInBasket && (
            <Box display="flex" flexDirection="column">
              <Box>
                <Box display="flex" alignItems="center" gap="4px">
                  <BasketTotalPriceV2 data-test-total-price="" component="p" id="lbl_total_exculd_delivery_fee">
                    <b>
                      {formatPriceWithCurrency(selectedItems?.length ? basketPrice.amount : 0, basketPrice.currency)}
                    </b>
                  </BasketTotalPriceV2>
                  <Box
                    onClick={() => {
                      !showBasketSummaryModal && openBasketSummaryAnalytics(ANALYTICS.BASKET_SUMMARY_OPEN, {});
                      !loading && setShowBasketSummaryModal(!showBasketSummaryModal);
                    }}
                    sx={{cursor: 'pointer', display: 'flex'}}
                  >
                    <ChevronUpDownIcon />
                  </Box>
                </Box>
              </Box>
              <When condition={showDeliveryFee && hasProductInBasket && selectedItems?.length}>
                {deliveryFees === '0.00 ฿' ? (
                  <DeliveryFeesLabel data-test-delivery-fee="" display="flex">
                    <DeliveryFeesLabel sx={{color: theme.palette.success.main}}>
                      {t('label.free')}&nbsp;
                    </DeliveryFeesLabel>
                    {t('label.delivery')}
                  </DeliveryFeesLabel>
                ) : (
                  <DeliveryFeesLabel data-test-delivery-fee="">
                    {t('message.deliveryFeeIncludedNew', {fees: deliveryFees})}
                  </DeliveryFeesLabel>
                )}
              </When>
            </Box>
          )}

          <Box>
            <Button
              data-test-checkout-btn=""
              disabled={disableCheckoutBtn || loading || !selectedItems?.length}
              fullWidth
              onClick={onCheckoutClick}
              type="button"
              color="primary"
              loading={loading}
              variant="contained"
              shape="rounded"
              id="btn_go_to_checkout"
            >
              {actionButtonLabel}
              <When condition={hasProductInBasket}>{` (${selectedItems?.length || 0})`}</When>
            </Button>
          </Box>
        </StyleFooterContentWrapper>
        <When condition={shouldShowMinBasketWarning && isLessThanMinSpend}>
          <Box paddingTop={1} paddingX={2.5} display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            <Typography fontWeight={400} fontSize={12}>
              {t('recommendations.minimumOrder', {amount: minimumBasket})}
            </Typography>
            <Box display="flex" alignItems="center" onClick={toggleMinimalBasketModal}>
              <Typography fontWeight={400} fontSize={12} color="common.mds_global_color_red_25">
                {t('recommendations.readMore')}
              </Typography>
              <ChevronRight
                width={12}
                height={12}
                fillColor={themeConfig.palette?.common?.mds_global_color_red_25}
              ></ChevronRight>
            </Box>
          </Box>
        </When>
      </Box>
      <MinimumBasketAmountModal
        open={isMinimalBasketModalOpen}
        onClose={toggleMinimalBasketModal}
        isReactNativeWebView={isReactNativeWebView}
      />
    </StyledBasketFooterWrapper>
  );
};

FooterView.displayName = 'FooterView';

export default FooterView;
